import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import MetaTags from '@/components/MetaTags';
import axios from 'axios';
import { API } from '@/App';

const cityBadgeColors = {
  'Miami': '#FF6B6B',
  'Austin': '#FFB347',
  'New York': '#1E90FF',
  'Nashville': '#22C55E',
  'Chicago': '#00F5FF',
  'San Diego': '#A855F7',
  'Denver': '#A855F7',
};

const Eyebrow = ({ children }) => (
  <span className="flex items-center justify-center gap-3 font-mono uppercase text-xs tracking-[3px]" style={{ color: 'rgba(8,0,16,0.6)' }}>
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    {children}
  </span>
);

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
    setLoading(false);
  };

  const getBadgeColor = (cityTag) => {
    return cityBadgeColors[cityTag] || '#FF2D78';
  };

  return (
    <div className="min-h-screen pt-24" data-testid="blog-page">
      <MetaTags
        title="Blog — City Guides & Nightlife Tips"
        description="Bar crawl tips, city guides, and nightlife stories from the Crawl Baby Crawl crew. Everything you need to own your night."
        path="/blog"
      />
      {/* Hero — Cyan with watermark */}
      <section className="py-20 px-4 text-center relative overflow-hidden" style={{ backgroundColor: '#00F5FF' }} data-testid="blog-hero">
        {/* CITY GUIDES watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[120px] md:text-[200px] lg:text-[260px] uppercase leading-none whitespace-nowrap" style={{ color: 'rgba(8,0,16,0.06)' }}>
            City Guides
          </span>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <Eyebrow>City Guides & Tips</Eyebrow>
          <h1 className="font-display text-[48px] md:text-[96px] uppercase mt-2 leading-none font-bold" style={{ color: '#080010' }}>
            The Blog
          </h1>
          <p className="text-lg mt-4 max-w-2xl mx-auto font-body" style={{ color: 'rgba(8,0,16,0.7)' }}>
            Insider guides to the best bars, crawl tips, and nightlife hacks from cities across America.
          </p>
        </motion.div>
      </section>

      <div style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Posts Grid — Warm off-white */}
      <section className="px-4 py-24" style={{ backgroundColor: '#F5F0EE' }} data-testid="blog-posts-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#FF2D78] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-[#555] text-xl">No posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-[16px] overflow-hidden group transition-all duration-200 ease-out hover:shadow-lg hover:-translate-y-1.5"
                  style={{ borderBottom: '3px solid transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = '#FF2D78'}
                  onMouseLeave={(e) => e.currentTarget.style.borderBottomColor = 'transparent'}
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <Badge
                        className="absolute top-3 left-3 text-white border-0"
                        style={{ backgroundColor: getBadgeColor(post.city_tag) }}
                      >
                        <MapPin size={12} className="mr-1" />
                        {post.city_tag}
                      </Badge>
                    </div>

                    <div className="p-6">
                      <h2 className="font-display text-xl uppercase transition-colors font-bold" style={{ color: '#080010' }}>
                        {post.title}
                      </h2>
                      <p className="text-[#777] mt-2 line-clamp-2 font-body">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-1 text-[#999] text-sm font-mono">
                          <Clock size={14} />
                          <span>{post.read_time} min read</span>
                        </div>
                        <span className="flex items-center gap-1 text-sm font-bold uppercase tracking-widest transition-all duration-150" style={{ color: '#FF2D78' }}>
                          Read <ArrowRight size={14} className="transition-transform duration-150 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
